export interface HabitOrderItem {
  id: string
  pinned: boolean
  sort_order: number
  created_at: string
}

export function normalizeHabitOrder(
  pinned: boolean | null | undefined,
  sortOrder: number | null | undefined,
): { pinned: boolean; sort_order: number } {
  return {
    pinned: pinned ?? false,
    sort_order: sortOrder ?? 0,
  }
}

export function sortHabitsByOrder<T extends HabitOrderItem>(habits: T[]): T[] {
  return [...habits].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1
    if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order
    return a.created_at.localeCompare(b.created_at)
  })
}

export function reorderHabitByDrop<T extends HabitOrderItem>(
  habits: T[],
  activeHabitId: string,
  overHabitId: string,
  scopedHabitIds: string[],
): T[] {
  const sortedHabits = sortHabitsByOrder(habits)
  const activeHabit = sortedHabits.find((habit) => habit.id === activeHabitId)
  const overHabit = sortedHabits.find((habit) => habit.id === overHabitId)

  if (
    !activeHabit ||
    !overHabit ||
    activeHabit.id === overHabit.id ||
    activeHabit.pinned !== overHabit.pinned
  ) {
    return sortedHabits
  }

  const scopedIdSet = new Set(scopedHabitIds)

  if (!scopedIdSet.has(activeHabit.id) || !scopedIdSet.has(overHabit.id)) {
    return sortedHabits
  }

  const pinnedGroupHabits = sortedHabits.filter(
    (habit) => habit.pinned === activeHabit.pinned,
  )
  const scopedPinnedHabitIds = pinnedGroupHabits
    .filter((habit) => scopedIdSet.has(habit.id))
    .map((habit) => habit.id)
  const currentIndex = scopedPinnedHabitIds.indexOf(activeHabit.id)
  const nextIndex = scopedPinnedHabitIds.indexOf(overHabit.id)

  if (currentIndex < 0 || nextIndex < 0) return sortedHabits

  const reorderedScopedIds = [...scopedPinnedHabitIds]
  const [movingHabitId] = reorderedScopedIds.splice(currentIndex, 1)
  reorderedScopedIds.splice(nextIndex, 0, movingHabitId)

  const reorderedIdQueue = [...reorderedScopedIds]
  const scopedPinnedIdSet = new Set(scopedPinnedHabitIds)
  const reorderedPinnedGroupIds = pinnedGroupHabits.map((habit) =>
    scopedPinnedIdSet.has(habit.id)
      ? (reorderedIdQueue.shift() ?? habit.id)
      : habit.id,
  )

  const orderByHabitId = new Map(
    reorderedPinnedGroupIds.map((habitId, index) => [habitId, index]),
  )

  return sortHabitsByOrder(
    sortedHabits.map((habit) =>
      orderByHabitId.has(habit.id)
        ? { ...habit, sort_order: orderByHabitId.get(habit.id) ?? 0 }
        : habit,
    ),
  )
}
